/**
 * Stacked shell styles for the main onboarding hero (`onboarding/MainScreen.tsx`) and theme re-exports.
 */
import { Platform, StyleSheet, type TextStyle, type ViewStyle } from 'react-native';
import { appTheme } from '../appTheme';
import type { ThemeColors } from '../store';
import { androidStripElevationSeams } from '../blurParity';
import { androidScaleSize } from '../androidUiParity';
import { hexToRgba } from '../colorUtils';

export function heroShellFrostedGradientColors(c: ThemeColors): [string, string] {
  return [c.secondaryFade, c.secondary];
}

const { spacing: s, borderRadius: br, sizes: sz } = appTheme;

const shell_gutter = s.lg;
const bottom_auth_row_width_frac = 0.88;
const bottom_auth_row_max_w = Math.round(720 * bottom_auth_row_width_frac);
const hero_cluster_gap = s.lg;
const action_row_h = sz.buttonHeightLarge + s.lg + s.lg;

export const HERO_CARD_SHELL_BOTTOM_RADIUS = 66;
export const hero_shell_control_radius = 24;
export const consult_row_bottom_corner_radius = 58;
export const shadow_style_android = {
  ...androidStripElevationSeams(),
  elevation: 2,
  shadowColor: appTheme.colors.black,
  shadowOpacity: 0.5,
  shadowRadius: 1,
  shadowOffset: { width: 1, height: 2 },
}
export const hero_shadow_android = {
  ...androidStripElevationSeams(),
  elevation: 6,
  shadowColor: appTheme.colors.black,
  shadowOpacity: 0.5,
  shadowRadius: 5,
  shadowOffset: { width: 0, height: 8 },
}
export function createStackedRadialMenuShellStyles(c: ThemeColors) {
  const shadow_scroll: ViewStyle =
    Platform.OS === 'android'
      ? hero_shadow_android
      : {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 8 },
          shadowOpacity: 0.35,
          shadowRadius: 12,
          // elevation: 10,
        };
  const shadow_auth_btn: ViewStyle =
    Platform.OS === 'android'
      ? shadow_style_android 
      : {
          shadowColor: c.shadowColor,
          shadowOffset: { width: 0, height: 2 },
          shadowOpacity: 0.4,
          shadowRadius: 6,
          // elevation: 5,
        };

  const inner_fill: ViewStyle = { flex: 1, width: '100%', justifyContent: 'center', alignItems: 'center' };

  const action_row_surface: ViewStyle = {
    width: '100%',
    alignSelf: 'stretch',
    minHeight: action_row_h,
    maxHeight: action_row_h,
    backgroundColor: 'transparent',
    borderTopLeftRadius: hero_shell_control_radius,
    borderTopRightRadius: hero_shell_control_radius,
    borderBottomLeftRadius: hero_shell_control_radius,
    borderBottomRightRadius: hero_shell_control_radius,
    zIndex: 15,
    ...shadow_auth_btn,
  };

  const shell_action_label = {
    flex: 1,
    color: c.primaryDark,
    fontSize: 14,
    fontWeight: '600' as const,
    letterSpacing: 0.5,
    textTransform: 'capitalize' as const,
    textAlign: 'center' as const,
  };

  const action_row_label_base: TextStyle = {
    flex: 1,
    minWidth: 0,
    color: c.primaryDark,
    fontSize: 14,
    fontWeight: '600',
    letterSpacing: 0.9,
    textTransform: 'capitalize',
    lineHeight: 20,
    paddingHorizontal: s.xl,
  };

  return StyleSheet.create({
    root: { flex: 1, backgroundColor: c.background, justifyContent: 'flex-start' },
    bgTile: { ...StyleSheet.absoluteFillObject, opacity: 0.16 },
    /**
     * Non-scrolling main column (`MainScreen` v3): shell shadow — **no** `minHeight: '100%'` / `flexGrow` on this `View`
     * (those belong on `ScrollView` `contentContainerStyle` only; otherwise the auth bar can be pushed off-screen).
     */
    heroColumn: { flex: 1, minHeight: 0, width: '100%', paddingTop: 0, justifyContent: 'flex-start', ...shadow_scroll },
    cardInner: {
      width: '92%',
      maxWidth: 720,
      alignSelf: 'center',
      marginBottom: s.md,
      flexDirection: 'column',
      paddingHorizontal: shell_gutter,
      paddingBottom: shell_gutter,
      borderBottomLeftRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
      borderBottomRightRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
      // Menu pill art overflows upward; `hidden` clips icons (Android prod + iOS).
      overflow: 'visible',
    },
    cardIn2: {
      borderWidth: StyleSheet.hairlineWidth,
      borderColor: 'transparent',
      borderBottomWidth: 0.75,
      borderBottomColor: c.primaryDark,
    },
    langSectionOuter: { width: '100%', marginBottom: shell_gutter, zIndex: 20, alignItems: 'center' },
    cardBottomCluster: {
      width: '100%',
      gap: hero_cluster_gap,
      alignItems: 'stretch',
      flexShrink: 0,
      zIndex: 14,
      overflow: 'visible',
    },
    cardRadialLayer: {
      ...StyleSheet.absoluteFillObject,
      borderBottomLeftRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
      borderBottomRightRadius: HERO_CARD_SHELL_BOTTOM_RADIUS,
      borderWidth: 0,
      borderBottomWidth: 0,
      borderColor: 'transparent',
    },
    pattOverlay: { ...StyleSheet.absoluteFillObject, opacity: 0.75 },
    langBtn: {
      ...(Platform.OS === 'android'
        ? { minHeight: androidScaleSize(32) }
        : { minHeight: sz.breadcrumbHeight / 1.25 }),
      alignSelf: Platform.OS === 'android' ? 'flex-start' : 'center',
      borderRadius: br.round,
      borderWidth: 1,
      borderColor: c.secondary,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 20,
    },
    langBtnText: {
      width: '100%',
      ...(Platform.OS === 'android'
        ? { fontSize: androidScaleSize(12), paddingHorizontal: s.md }
        : { fontSize: 14, paddingHorizontal: s.lg }),
      fontWeight: '600',
      color: c.secondary,
      letterSpacing: 0.5,
      textAlign: 'center',
    },
    shellClickableInnerFillCenter: inner_fill,
    shellClickableInnerMenuPill: {
      flex: 1,
      flexBasis: 0,
      width: '100%',
      minWidth: 0,
      alignItems: 'center',
      justifyContent: 'flex-end',
      paddingBottom: s.sm,

    },
    shellClickableInnerActionRow: {
      flex: 1,
      width: '100%',
      minHeight: action_row_h,
      justifyContent: 'center',
      alignItems: 'center',
      overflow: 'hidden',
      borderRadius: hero_shell_control_radius,
      // borderWidth: 0,
      // borderColor: 'transparent',
      borderWidth: 0.5,
      borderColor: hexToRgba(c.secondaryHalf, 0.1),
    
    },
    actionRowContent: {
      flex: 1,
      width: '100%',
      minHeight: action_row_h,
      flexDirection: 'row',
      alignItems: 'center',
      paddingHorizontal: s.md,
      gap: s.md,
    },
    actionRowIconBadge: {
      width: action_row_h,
      minWidth: action_row_h,
      height: action_row_h,
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'transparent',
      position: 'absolute',
      top: 0,
      bottom: 0,
      zIndex: 10,
      transform: [{ rotate: '8deg' }, { scale: 1.25 }, { translateY: '15%' }],
      opacity: 0.5,
    },
    trailing: { right: '10%' },
    leading: { left: '10%' },
    actionRowLabelLeading: {
      ...action_row_label_base,
      textAlign: 'right',
    },
    actionRowLabelTrailing: {
      ...action_row_label_base,
      textAlign: 'left',
    },
    logoSection: {
      flex: 1,
      minHeight: 0,
      width: '100%',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 4,
    },
    logoBlock: {
      width: '90%',
      marginHorizontal: '5%',
      minHeight: Platform.OS === 'android' ? 48 : 80,
      maxHeight: 176,
      ...(Platform.OS === 'android'
        ? { flex: 1, flexShrink: 1 }
        : { height: '24%', flexShrink: 0 }),
      justifyContent: 'center',
      alignItems: 'center',
    },
    /** v3 hero — remote `organization.configurations.assets` logo image (when used). */
    orgLogoImage: { width: '100%', height: '100%' },
    /** Three equal shortcuts above journey/consult — image half overflows top edge of each box. */
    menuShortcutRow: {
      flexDirection: 'row',
      alignItems: 'flex-end',
      width: '100%',
      gap: s.sm,
      paddingTop: Platform.OS === 'android' ? androidScaleSize(38) : 38,
      paddingBottom: s.xs,
      zIndex: 12,
      overflow: 'visible',
      ...(Platform.OS === 'android' ? { collapsable: false } : null),
    },
    /** Slot wraps art + pill so overflow art is not clipped by Android `Pressable` corner masks. */
    menuShortcutSlot: {
      flex: 1,
      flexBasis: 0,
      position: 'relative',
      overflow: 'visible',
      ...(Platform.OS === 'android' ? { collapsable: false } : null),
    },
    menuShortcutBtn: {
      // width: '100%',
      height: 60,
      borderRadius: br.lg,
      backgroundColor: c.secondaryHalf,
      borderWidth: 1,
      borderColor: c.secondary,
      alignItems: 'center',
      justifyContent: 'flex-end',
      // paddingHorizontal: s.xs,
      ...(Platform.OS === 'ios' ? shadow_auth_btn : shadow_style_android),
      // ...action_row_surface,
      // overflow: 'hidden',
    },
    menuShortcutImageWrap: {
      position: 'absolute',
      top: Platform.OS === 'android' ? -androidScaleSize(56) : -56,
      left: 0,
      right: 0,
      alignItems: 'center',
      zIndex: 4,
      ...(Platform.OS === 'android' ? { elevation: 6, collapsable: false } : null),
    },
    menuShortcutImage: {
      width: Platform.OS === 'android' ? androidScaleSize(90) : 90,
      height: Platform.OS === 'android' ? androidScaleSize(102) : 102,
    },
    menuShortcutLabel: {
      fontSize: Platform.OS === 'android' ? androidScaleSize(12) : 14,
      lineHeight: Platform.OS === 'android' ? androidScaleSize(12) : 14,
      fontWeight: '600',
      color: c.primaryDark,
      textAlign: 'center',
      letterSpacing: 0.6,
      textTransform: 'capitalize',
      paddingHorizontal: 2,
      zIndex: 1,
    },
    journeyRow: {
      ...action_row_surface,
    },
    consultRow: {
      ...action_row_surface,
      zIndex: 16,
      borderBottomLeftRadius: consult_row_bottom_corner_radius,
      borderBottomRightRadius: consult_row_bottom_corner_radius,
    },
    bottomAuthBar: {
      width: `${bottom_auth_row_width_frac * 90}%`,
      maxWidth: bottom_auth_row_max_w,
      alignSelf: 'center',
      paddingHorizontal: s.xs,
      flexDirection: 'row',
      gap: s.md,
      justifyContent: 'center',
      alignItems: 'stretch',
      // Web: sit above MAIN_SCREEN_DOCK.floatZ (500) so auth chips stay pressable over the dock.
      zIndex: Platform.OS === 'web' ? 510 : 45,
    },
    bottomAuthBtn: {
      flex: 1,
      minWidth: 0,
      flexBasis: 0,
      maxWidth: 380,
      /** Room for 2-line labels on stacked-hero auth chips without clipping. */
      minHeight: Platform.OS === 'android' ? androidScaleSize(40) : 48,
      borderRadius: hero_shell_control_radius,
      backgroundColor: c.secondary,
      borderWidth: 0,
      borderColor: c.secondary,
      justifyContent: 'center',
      alignItems: 'center',
      ...shadow_auth_btn,
      zIndex: Platform.OS === 'web' ? 510 : 45,
    },
    shellClickableInnerAuthChip: { ...StyleSheet.absoluteFillObject },
    bottomAuthStack: {
      flex: 1,
      width: '100%',
      position: 'relative',
      justifyContent: 'center',
      alignItems: 'center',
    },
    bottomAuthBtnLabelWrap: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      paddingHorizontal: s.md,
      zIndex: 0,
    },
    bottomAuthBtnChevron: {
      ...StyleSheet.absoluteFillObject,
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1,
    },
    bottomAuthLabel: {
      ...shell_action_label,
      fontSize: 16,
      lineHeight: 20,
      color: c.primaryDark,
      flex: 1,
      minWidth: 0,
    },
    merakFooter: {
      position: 'absolute',
      bottom: 0,
      left: 0,
      right: 0,
      minHeight: 88,
      paddingBottom: 0,
      justifyContent: 'flex-end',
      alignItems: 'center',
      zIndex: 40,
    },
    merakFooterText: { fontSize: 12, color: c.textSecondary, opacity: 0.92, paddingBottom: s.lg },
  });
}

export type StackedRadialMenuShellStyles = ReturnType<typeof createStackedRadialMenuShellStyles>;
